/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState } from 'react';
import axios from 'axios';
import './Login.css';

// --- SVG Icons ---
const Icons = {
    Logo: () => (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4CAF50' }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <path d="M9 15l2 2 4-4"></path>
        </svg>
    ),
    Eye: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
    ),
    EyeOff: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
    )
};

export default function Login({ onLoginSuccess }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!username || !password) {
            setError('Please enter both username and password');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/login.php`,
                { username, password }
            );

            if (response.data.success) {
                // Store auth state
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('username', response.data.username);
                localStorage.setItem('is_admin', response.data.is_admin ? 'true' : 'false');
                localStorage.setItem('is_hod', response.data.is_hod ? 'true' : 'false');
                localStorage.setItem('user_role', response.data.user_role || 'teacher');
                localStorage.setItem('auth_token', response.data.auth_token);
                axios.defaults.headers.common.Authorization = `Bearer ${response.data.auth_token}`;
                localStorage.setItem('user_id', response.data.user_id);
                localStorage.setItem('school_id', response.data.school_id || '1');
                onLoginSuccess();
            } else {
                setError(response.data.error || 'Invalid credentials');
            }
        } catch (err) {
            setError('Login failed. Server unavailable.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-wrapper">
            <div className="login-card">
                <div className="brand-section">
                    <img src="assets/logo.webp" alt="Marksure Logo" className="login-logo" width="100" height="100" />
                    <h1>Marksure</h1>
                    <p>Staff Login Portal</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    {error && <div className="error-alert">{error}</div>}

                    <div className="input-group">
                        <label htmlFor="username">Username / Email</label>
                        <input
                            id="username"
                            type="text"
                            className="modern-input"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="e.g. teacher@school.co.za"
                            disabled={loading}
                            autoComplete="username"
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="password">Password</label>
                        <div className="password-wrapper">
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                className="modern-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter password"
                                disabled={loading}
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex={-1}
                            >
                                {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="btn-login"
                        disabled={loading}
                    >
                        {loading ? 'Authenticating...' : 'Sign In'}
                    </button>
                </form>

                <div className="login-footer">
                    <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px', textAlign: 'center' }}>
                        Marksure is a privately owned productivity tool. It is not an official system of the Department of Basic Education or SASAMS. Users are responsible for verifying all data against official records.
                    </p>
                    <p>Don't have an account? <a href="/subscription">Subscribe here</a></p>
                    <p><a href="/privacy-policy" className="privacy-link">Privacy Policy</a></p>
                </div>
            </div>
        </div>
    );
}
